/** @jsx jsx */
import { jsx } from '@emotion/core'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { ChatFontSize, ChatMessageState, ChatMessageType, Schema$ChatMessage } from '@interface/chat'
import * as _ from 'lodash'
import uuid from 'uuid'
import SYMBOL_ICO from '@assets/images/icon/meta_image/symbol.png'
import BettingListBg from '@assets/images/bg/shared-betting-history-bg.png'
import { makeATagAutoGenerationElements } from '@utils/jsxutils'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import PopupUtils from '@utils/popup-utils'
import { Button } from '@styles/button.style'
import { UserPickSpecialMemberMark } from '@views/Home/UserPick/Components/UserPickSpecialMemberMark'
import {
  BettingListStyle,
  ChatLoginBtn,
  ChatTodayUserPickListItemStyle,
} from '@views/Popup/Chat/index.style'
import replyNickIco from '@assets/images/icon/chat_icon/reply-nick-ico.png'
import SimpleBettingHistory from '@components/SimpleBettingHistory'
import {
  makeAttachmentDetailObjToBettingRecordTrack,
} from '@utils/utils'
import Login from '@views/Login'
import { Schema$UserPickExtend } from '@interface/user-pick'
import ChatTodayUserPickBg from '@assets/images/bg/chat-today-user-pick-bg.png'
import { PickList } from '@views/Home/index.style'
import { UserPickItem } from '@views/Home/UserPick/Components/UserPickItem'
import { MESSAGE_ITEM_ORIGIN, PAGE_TYPE } from '@interface/common'
import useRootStore from '@store/useRootStore'
import { GlobalStoreKey, USER_TITLE_ID } from '@v2/constants/common'
import { CHAT_TAB_INDEX_KEY } from '@constants/index'
import { SimpleCheerForTeamMatchInfo } from 'components/SimpleCheerForTeamMatchInfo'
import { CHEER_FOR_TEAM_CONTENTS_TYPE } from '@v2/constants/cheer'
import * as ImageUtils from '@utils/image-utils'
import DialogUtils from '@utils/dialog-utils'
import { TIMELINE_POPUP_ENABLED_SPORT_IDS } from '@game-type/game-market-types'
import { MATCH_GAME_STATUS } from 'adv-protocol/src/GameCommon'
import AdvImg from '@components/AdvImg'

interface Props {
  chatMessage: Schema$ChatMessage
  onlyFavoriteUserMessage: boolean
  favoriteUserListObject: any
  blockUserListObject: any
  currentTabIndexKey: CHAT_TAB_INDEX_KEY
  chattingOptions: any
  fontSize: number
}

export const ChatContent: React.FC<Props> = observer((props): React.ReactElement => {
  const { authStore, globalStore, chattingStore } = useRootStore()
  const {
    chatMessage,
    onlyFavoriteUserMessage,
    favoriteUserListObject,
    blockUserListObject,
    currentTabIndexKey,
    chattingOptions,
    fontSize,
  } = props
  const {
    id: chatMessageId, chatId, state: msgState, visibleAttachment, visibleContents,
    type: chatType, attachment, contents, filteredContents,
    fromUserProfileImg, fromUserNick, toUserNick, fromUserId,
    attachmentDetail, sMemberMark, sMember, userTitleId,
    userTitleEnabled, cheerForTeamData, cheerForTeamContentsType,
  } = chatMessage

  const imageUrl = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL).value
  const CheerAddBtnImgUrl = `${imageUrl}btn/cheer_add_btn.png`

  // 이모티콘이나 gif 이미지 정지 후 재생을 위해 터치시 suffix 의 값을 바꿔 동작 시키기 위해.
  // json string [{chatId, suffix}]
  const [addSuffixList, setAddSuffixList] = useState('[]')
  const [
    chatMessageContentElements,
    setChatMessageContentElements,
  ] = useState<React.ReactElement[]>()
  const [isSocialMedia, setIsSocialMedia] = useState<boolean>(false)

  const btnRefs = useRef({})

  const attachImgUrlPrefix = useMemo<string>(() => {
    return globalStore.findGlobalSetting(GlobalStoreKey.CHAT_ATTACH_IMAGE_URL_PREFIX)?.value
  }, [])
  const attachEmoticonUrlPrefix = useMemo<string>(() => {
    return globalStore.findGlobalSetting(GlobalStoreKey.CHAT_EMOTICON_URL_PREFIX)?.value
  }, [])

  useEffect(() => {
    let chatMessageContents = filteredContents || contents
    if (!visibleContents && chatMessageContents) {
      chatMessageContents = chattingOptions.invisibleContentsMessage
    }

    setChatMessageContentElements(makeATagAutoGenerationElements(globalStore, chatMessageContents, false, () => {
      setIsSocialMedia(true)
      setChatMessageContentElements(makeATagAutoGenerationElements(globalStore, chatMessageContents, true))
    }))
  }, [])

  const currentUserId = authStore?.userInfo.id

  let fromProfileImg = fromUserProfileImg
  if (fromUserProfileImg?.indexOf('randomuser.me/api/portraits/men/46.jpg') > -1) {
    fromProfileImg = 'https://d1i7hydgfclv7m.cloudfront.net/data_center/item_image_m/FB24E425-3167-4AA6-AC12-56CA9DECEC7A.png'
  }

  const isMine = currentUserId === fromUserId

  if (msgState === ChatMessageState.DISABLE) {
    return undefined
  }
  if (!isMine && _.find(blockUserListObject, { id: fromUserId })) {
    return undefined
  }
  if (!isMine
    && chatMessage.fromUserId !== MESSAGE_ITEM_ORIGIN.SYSTEM
    && onlyFavoriteUserMessage && !_.find(favoriteUserListObject, { id: fromUserId })
  ) {
    return undefined
  }
  const attachmentDetailObj = attachmentDetail ? JSON.parse(attachmentDetail) as any : undefined
  let attachImgSrc = SYMBOL_ICO
  let attachEmoticonSrc = SYMBOL_ICO
  let betHistoryImgSrc = SYMBOL_ICO

  if (visibleAttachment) {
    switch (chatType) {
      case ChatMessageType.IMAGE:
        attachImgSrc = `${attachImgUrlPrefix}${attachment}`
        break
      case ChatMessageType.EMOTICON: {
        const suffixObj = JSON.parse(addSuffixList)
        const suffix = _.find(suffixObj, { chatId })
        attachEmoticonSrc = `${attachEmoticonUrlPrefix}${attachment}?i=${chatId}&t=${suffix ? suffix.suffix : ''}`
        break
      }
      case ChatMessageType.BET_HISTORY:
        betHistoryImgSrc = BettingListBg
        break
      case ChatMessageType.CHEER_FOR_TEAM: {
        if (cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.EMOTICON) {
          const suffixObj = JSON.parse(addSuffixList)
          const suffix = _.find(suffixObj, { chatId })
          attachEmoticonSrc = `${attachEmoticonUrlPrefix}${attachment}?i=${chatId}&t=${suffix ? suffix.suffix : ''}`
        } else if (cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.IMAGE) {
          attachImgSrc = `${attachImgUrlPrefix}${attachment}`
        } else if (cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.BET_HISTORY) {
          betHistoryImgSrc = BettingListBg
        }
        break
      }
    }
  }

  const elemKey = `chatId_${chatId}_${currentTabIndexKey}`
  const popoverKey = `${elemKey}_popover`
  let fontSizeClassName: string
  switch (fontSize) {
    case ChatFontSize.LARGE:
      fontSizeClassName = 'large'
      break
    default:
      fontSizeClassName = 'small'
      break
  }

  const isCustomProfileImg = fromProfileImg.indexOf('user_profile') > -1
  let userProfileImgClassName = 'user-profile-btn'
  if (_.find(favoriteUserListObject, { id: fromUserId })) {
    userProfileImgClassName += ' active'
  }

  const onLoadChatBubbleImg = (e) => {
    e.currentTarget.style.height = 'auto'
  }

  const chatListItemClass = classNames(
    'chat-list--item',
    { 'chat-list--item pc-pick-message': chatType === ChatMessageType.USER_PICK && !isMobile },
    { 'chat-list--item my-message': isMine },
  )

  const onClickUserDetailInfoPopup = () => {
    PopupUtils.showUserDetailInfo(fromUserId)
  }

  const addEmoticonSuffixHandler = (chatId) => {
    const suffixObj = JSON.parse(addSuffixList)
    let suffix = _.find(suffixObj, { chatId })
    if (suffix) {
      suffix.suffix = uuid.v4()
      _.remove(suffixObj, { chatId })
    } else {
      suffix = { chatId, suffix: uuid.v4() }
    }
    suffixObj.push(suffix)
    setAddSuffixList(JSON.stringify(suffixObj))
  }

  // 공유된 베팅 내역 이벤트
  const onClickOpenBettingListInfo = async (betId: string, fromUserNick: string) => {
    DialogUtils.showSharedBettingList({ betId, fromUserNick })
  }

  // 공유된 이미지 보기
  const onClickViewImg = (imageSrc: string) => {
    // 추가BET 과 이미지미리보기 팝업은 동일한 setGeneralPopupProperties 를 사용하고 있어 문제가 발생.
    // 추가BET 팝업에서 채팅창을 띄운뒤 채팅에 첨부된 이미지 미리보기 팝업을 띄우고 해당 이미지를 닫기를 선택시
    // 추가BET 화면도 함께 닫히는 문제가 있어 아래의 pushDialogOk 함수를 이용하여 첨부된 이미지를 보도록 한다.
    DialogUtils.showImagePreview({ imageSrc })
  }

  const chatBubbleClassName = classNames(
    'chat-bubble',
    { 'social-media': isSocialMedia },
    { 'social-media mo': isSocialMedia || isMobile },
    { 'pc': !isMobile },
    { 's-user': userTitleEnabled && sMemberMark },
    // { 'hard-user': userTitleEnabled && _.isNil(sMember) && userTitleId === USER_TITLE_ID.HARD_ACTIVIST },
    { 'premium-user': userTitleEnabled && _.isNil(sMember) && userTitleId === USER_TITLE_ID.PREMIUM_ANALYST },
    { 'is-cheer-for-team': chatType === ChatMessageType.CHEER_FOR_TEAM },
    { 'is-betting-history': chatType === ChatMessageType.BET_HISTORY },
  )
  let chatListItem: React.ReactElement

  const onClickLoginHandler = () => {
    authStore.showLoginPopup(<Login/>)
  }

  const isTimelinePopupEnabled = TIMELINE_POPUP_ENABLED_SPORT_IDS.includes(cheerForTeamData?.sportId)

  const onClickCheerForTeamInfo = (): void => {
    if (!isTimelinePopupEnabled) {
      return
    }

    const {
      matchId,
      sportId,
      homeScore,
      awayScore,
      homeName: homeTeamName,
      awayName: awayTeamName,
      homeEmblem: homeTeamEmblem,
      awayEmblem: awayTeamEmblem,
      matchStatusName,
    } = cheerForTeamData


    const isLiveMatch = cheerForTeamData.status === MATCH_GAME_STATUS.Live
    const matchRecordSimpleMatchInfo = {
      matchId,
      sportId,
      homeScore,
      awayScore,
      homeTeamName,
      awayTeamName,
      homeTeamEmblem,
      awayTeamEmblem,
      matchStatusName,
      isLiveMatch,
    }

    DialogUtils.showMatchRecord(matchRecordSimpleMatchInfo)
  }

  const onClickCheerForTeamBtn = (): void => {
    if (!authStore.isSignedIn) {
      onClickLoginHandler()
    } else {
      globalStore.clearActivityScoreNoticeQueue()
      if (chattingStore.chattingOptions.activeCheerForTeam) {
        chattingStore.getCheerForTeamMatchInfo(cheerForTeamData.matchId).then(refreshedCheerForTeamData => {
          const defaultEffectIcon = ImageUtils.getDefaultCheerForTeamEffectIcon()
          refreshedCheerForTeamData.effectIconUrl = defaultEffectIcon.img
          chattingStore.setSelectedCheerForTeamItem(refreshedCheerForTeamData)
        })
      }
    }
  }

  const imageContentElem = (): React.ReactElement => {
    return (
      <div className="chat-bubble--upload">
        <Button onClick={() => onClickViewImg(attachImgSrc)}>
          <AdvImg src={attachImgSrc} srcMode={'small'} alt="업로드 이미지" className="chat-bubble--upload--img" onLoad={onLoadChatBubbleImg}/>
        </Button>
      </div>
    )
  }

  const emoticonContentElem = (): React.ReactElement => {
    return (
      <div className="chat-bubble--upload">
        <img
          src={attachEmoticonSrc}
          alt="이모티콘"
          className="chat-bubble--upload--img"
          onClick={() => addEmoticonSuffixHandler(chatId)}
          onLoad={onLoadChatBubbleImg}
        />
      </div>
    )
  }

  const betHistoryContentELem = (): React.ReactElement => {
    if (visibleAttachment) {
      return (
        <SimpleBettingHistory
          bettingRecordTrack={makeAttachmentDetailObjToBettingRecordTrack(attachmentDetailObj)}
          onClickOpenBettingListInfo={() => onClickOpenBettingListInfo(attachment, fromUserNick)}
          className={!isMobile ? 'pc-betting-list' : 'betting-list'}
        />
      )
    } else {
      return (
        <Button css={BettingListStyle} className={!isMobile ? 'pc-betting-list chat-bubble' : 'chat-bubble'}>
          <img src={betHistoryImgSrc} alt="베팅 내역 공유 이미지" className="betting-list-bg"/>
        </Button>
      )
    }
  }

  if (chatType !== ChatMessageType.USER_PICK) {
    chatListItem = (
      <Fragment>
        {!isMine ?
          <Button className={userProfileImgClassName} ref={el => {
            btnRefs.current[popoverKey] = el
          }} onClick={onClickUserDetailInfoPopup}>
            <div className="profile-img-wrap lhn3">
              {isCustomProfileImg ?
                (<AdvImg src={fromProfileImg} srcMode={'small'} alt="사진첩 프로필" className="profile-img custom-img"/>)
                :
                (<AdvImg src={fromProfileImg} srcMode={'small'} alt="스킨 프로필" className="profile-img"/>)
              }
            </div>
            <UserPickSpecialMemberMark
              showSMark={sMemberMark}
              isSpecialMember={!_.isNil(sMember)}
              userTitleId={userTitleId}
              userTitleEnabled={userTitleEnabled}
              medium
              lightBorder/>
          </Button>
          :
          <Button className={userProfileImgClassName} ref={el => {
            btnRefs.current[popoverKey] = el
          }} onClick={onClickUserDetailInfoPopup}>
            <div className="profile-img-wrap lhn4">
              {isCustomProfileImg ?
                (<AdvImg src={fromProfileImg} srcMode={'small'} alt="사진첩 프로필" className="profile-img custom-img"/>)
                :
                (<AdvImg src={fromProfileImg} srcMode={'small'} alt="스킨 프로필" className="profile-img"/>)
              }
            </div>
            <UserPickSpecialMemberMark
              showSMark={sMemberMark}
              isSpecialMember={!_.isNil(sMember)}
              userTitleId={userTitleId}
              userTitleEnabled={userTitleEnabled}
              medium
              lightBorder/>
          </Button>
        }
        {!authStore.isSignedIn && <div css={ChatLoginBtn} className="chat-profile"/>}

        <div className="chat-contents">
          {/* *************** 닉네임 *************** */}
          <div className="nick-info">
            <span className="nick-name">{fromUserNick}</span>
            {/* *************** 대상 지정하여 채팅했을 경우 *************** */}
            {toUserNick &&
              <span className="reply-nick-name">
                <img src={replyNickIco} alt="답장" className="reply-nick-name--img"/>
                {toUserNick}
              </span>
            }
          </div>

          {/* *************** 채팅 말풍선 영역 *************** */}
          <div className={chatBubbleClassName}>
            {/* *************** 업로드 응원글 *************** */}
            {chatType === ChatMessageType.CHEER_FOR_TEAM && (
              <Fragment>
                <Button TP
                        className={isTimelinePopupEnabled
                          ? 'chat-bubble--upload--cheer-for-team-btn'
                          : 'chat-bubble--upload--cheer-for-team-btn is-not-match-record'}
                        onClick={onClickCheerForTeamInfo}>
                  <SimpleCheerForTeamMatchInfo
                    chatMessageId={chatMessageId}
                    selectedCheerForTeamData={cheerForTeamData}
                  />
                </Button>
                {cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.IMAGE && imageContentElem()}
                {cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.EMOTICON && emoticonContentElem()}
                {cheerForTeamContentsType === CHEER_FOR_TEAM_CONTENTS_TYPE.BET_HISTORY && betHistoryContentELem()}
              </Fragment>
            )}

            {/* *************** 업로드 이미지 / 이모티콘 *************** */}
            {chatType === ChatMessageType.IMAGE && imageContentElem()}
            {chatType === ChatMessageType.EMOTICON && emoticonContentElem()}
            {/* *************** 공유된 베팅 내역 *************** */}
            {chatType === ChatMessageType.BET_HISTORY && betHistoryContentELem()}
            {/* *************** 대화 내용 *************** */}
            <p className={`chat-bubble--txt ${fontSizeClassName}`}>{chatMessageContentElements}</p>
            {chatType === ChatMessageType.CHEER_FOR_TEAM &&
              <Button TP className="chat-bubble--cheer-for-team-btn" onClick={onClickCheerForTeamBtn}>
                <img className="cheer-btn-img" src={CheerAddBtnImgUrl} alt="응원 추가하기 버튼"/>
              </Button>
            }
          </div>
        </div>
      </Fragment>
    )
  } else {
    const onClickLoginHandler = () => {
      authStore.showLoginPopup(<Login/>)
    }

    const onClickChatListItemTodayUserPick = () => {
      if (!authStore.isSignedIn) {
        onClickLoginHandler()
      } else {
        PopupUtils.showTodayUserPick()
      }
    }

    const userPick = attachmentDetailObj as Schema$UserPickExtend
    const userNick = userPick.pickOwner.userNick
    chatListItem = (
      <Button TP onClick={onClickChatListItemTodayUserPick} css={ChatTodayUserPickListItemStyle}
              className={!isMobile && 'pc'}>
        <div className="top-txt">
          <img src={ChatTodayUserPickBg} alt="오늘의 승부사"/>
          <div className="txt">
            <span className="nickname">{userNick}</span>
            님 축하드립니다!
          </div>
        </div>

        <PickList className="user-pick-detail-chat">
          <UserPickItem
            userPick={userPick}
            pageType={PAGE_TYPE.USER_PICK_PROFILE}
            className={'popup-item'}
            showMatchInfoFull
          />
        </PickList>
      </Button>
    )
  }

  return (
    <li key={elemKey} className={chatListItemClass}>
      {chatListItem}
    </li>
  )
})
